/**
 * Period Filter Utilities
 *
 * Utilities for filtering financial period data (quarters/years) with support for:
 * - Last X periods (slider-based)
 * - Year-based filtering (e.g., all quarters in 2024)
 * - Custom range filtering (e.g., Q1-22 to Q4-23)
 */

/**
 * Extract year from a period string
 * Handles formats: Q1-21, Q2-22, FY21, FY2021, 2021, etc.
 *
 * @param {string} period - Period string (e.g., "Q1-21", "FY21", "2021")
 * @returns {string} - Two or four digit year string (e.g., "21", "2021")
 */
export const extractYear = (period) => {
  if (!period) return null;

  const periodStr = String(period);

  // Handle 2015-Q1, 2024-Q3 format (year-quarter with dash)
  const yearQuarterMatch = periodStr.match(/^(\d{4})-Q\d$/i);
  if (yearQuarterMatch) {
    return yearQuarterMatch[1];
  }

  // Handle Q1-21, Q2-22 format (quarter-year with dash)
  const quarterMatch = periodStr.match(/Q\d-(\d{2,4})/i);
  if (quarterMatch) {
    return quarterMatch[1];
  }

  // Handle FY21, FY2021 format (fiscal year)
  const fyMatch = periodStr.match(/FY(\d{2,4})/i);
  if (fyMatch) {
    return fyMatch[1];
  }

  // Handle plain year (2021, 21)
  const yearMatch = periodStr.match(/(\d{2,4})/);
  if (yearMatch) {
    return yearMatch[1];
  }

  return null;
};

/**
 * Normalize year to 2-digit format for consistent comparison
 *
 * @param {string} year - Year string (e.g., "2021" or "21")
 * @returns {string} - Two digit year (e.g., "21")
 */
export const normalizeYear = (year) => {
  if (!year) return null;
  const yearStr = String(year);
  if (yearStr.length === 4) {
    return yearStr.slice(-2);
  }
  return yearStr;
};

/**
 * Get unique years from a period mapping array, sorted descending (most recent first)
 *
 * @param {Array<{period: string}>} periods - Array of period objects with period property
 * @returns {Array<string>} - Array of unique years, most recent first
 */
export const getAvailableYears = (periods) => {
  if (!periods || !Array.isArray(periods)) return [];

  const yearsSet = new Set();

  periods.forEach(mapping => {
    const year = extractYear(mapping.period);
    if (year) {
      yearsSet.add(normalizeYear(year));
    }
  });

  // Convert to array and sort descending (most recent first)
  return Array.from(yearsSet).sort((a, b) => {
    const numA = parseInt(a, 10);
    const numB = parseInt(b, 10);
    return numB - numA;
  });
};

/**
 * Apply a period filter to a mapping array
 *
 * @param {Array<{period: string, fy?: string, endDate?: string}>} mapping - Full period mapping array
 * @param {{type: 'lastX'|'year'|'range', value?: number, start?: string, end?: string}} filter - Filter configuration
 * @returns {Array} - Filtered mapping array
 *
 * Filter types:
 * - { type: 'lastX', value: 8 } - Last X periods
 * - { type: 'year', value: '24' } - All periods in a specific year
 * - { type: 'range', start: 'Q1-22', end: 'Q4-23' } - Custom range (inclusive)
 */
export const applyPeriodFilter = (mapping, filter) => {
  if (!mapping || !Array.isArray(mapping) || mapping.length === 0) {
    return [];
  }

  if (!filter || !filter.type) {
    return mapping;
  }

  switch (filter.type) {
    case 'lastX': {
      const count = Math.min(filter.value || mapping.length, mapping.length);
      return mapping.slice(-count);
    }

    case 'year': {
      const targetYear = normalizeYear(filter.value);
      if (!targetYear) return mapping;

      return mapping.filter(m => {
        const periodYear = normalizeYear(extractYear(m.period));
        return periodYear === targetYear;
      });
    }

    case 'range': {
      const startPeriod = filter.start;
      const endPeriod = filter.end;

      if (!startPeriod || !endPeriod) return mapping;

      const startIndex = mapping.findIndex(m => m.period === startPeriod);
      const endIndex = mapping.findIndex(m => m.period === endPeriod);

      if (startIndex === -1 || endIndex === -1) {
        // If exact match not found, return empty or full array
        console.warn('Period range not found in mapping:', { startPeriod, endPeriod });
        return mapping;
      }

      // Ensure correct order (start should be <= end in array index)
      const actualStart = Math.min(startIndex, endIndex);
      const actualEnd = Math.max(startIndex, endIndex);

      return mapping.slice(actualStart, actualEnd + 1);
    }

    default:
      return mapping;
  }
};

/**
 * Get the count of periods that would be returned by a filter
 * Useful for displaying in UI without actually filtering
 *
 * @param {Array} mapping - Full period mapping array
 * @param {Object} filter - Filter configuration
 * @returns {number} - Count of periods
 */
export const getFilteredPeriodCount = (mapping, filter) => {
  return applyPeriodFilter(mapping, filter).length;
};

/**
 * Create a lastX filter object
 * @param {number} value - Number of periods
 * @returns {{type: 'lastX', value: number}}
 */
export const createLastXFilter = (value) => ({
  type: 'lastX',
  value,
});

/**
 * Create a year filter object
 * @param {string} year - Year to filter (e.g., "24" or "2024")
 * @returns {{type: 'year', value: string}}
 */
export const createYearFilter = (year) => ({
  type: 'year',
  value: normalizeYear(year),
});

/**
 * Create a range filter object
 * @param {string} start - Start period (e.g., "Q1-22")
 * @param {string} end - End period (e.g., "Q4-23")
 * @returns {{type: 'range', start: string, end: string}}
 */
export const createRangeFilter = (start, end) => ({
  type: 'range',
  start,
  end,
});

/**
 * Check if a filter is the default "lastX" type with a specific value
 * @param {Object} filter - Filter object
 * @param {number} value - Expected value
 * @returns {boolean}
 */
export const isLastXFilter = (filter, value) => {
  return filter?.type === 'lastX' && filter?.value === value;
};

/**
 * Check if a filter is a year filter with a specific year
 * @param {Object} filter - Filter object
 * @param {string} year - Expected year
 * @returns {boolean}
 */
export const isYearFilter = (filter, year) => {
  return filter?.type === 'year' && normalizeYear(filter?.value) === normalizeYear(year);
};

/**
 * Check if a filter is a custom range filter
 * @param {Object} filter - Filter object
 * @returns {boolean}
 */
export const isRangeFilter = (filter) => {
  return filter?.type === 'range';
};

/**
 * Apply a period filter to a data array with {x, y} structure (like CSP data)
 * where x is the period string (e.g., "2015-Q1")
 *
 * @param {Array<{x: string, y: any}>} dataArray - Array of data points
 * @param {Object} filter - Filter configuration
 * @returns {Array} - Filtered data array
 */
export const applyPeriodFilterToDataArray = (dataArray, filter) => {
  if (!dataArray || !Array.isArray(dataArray) || dataArray.length === 0) {
    return [];
  }

  if (!filter || !filter.type) {
    return dataArray;
  }

  switch (filter.type) {
    case 'lastX': {
      const count = Math.min(filter.value || dataArray.length, dataArray.length);
      return dataArray.slice(-count);
    }

    case 'year': {
      const targetYear = normalizeYear(filter.value);
      if (!targetYear) return dataArray;

      return dataArray.filter(point => {
        const pointYear = normalizeYear(extractYear(point.x));
        return pointYear === targetYear;
      });
    }

    case 'range': {
      const startPeriod = filter.start;
      const endPeriod = filter.end;

      if (!startPeriod || !endPeriod) return dataArray;

      const startIndex = dataArray.findIndex(point => point.x === startPeriod);
      const endIndex = dataArray.findIndex(point => point.x === endPeriod);

      if (startIndex === -1 || endIndex === -1) {
        console.warn('Period range not found in data array:', { startPeriod, endPeriod });
        return dataArray;
      }

      const actualStart = Math.min(startIndex, endIndex);
      const actualEnd = Math.max(startIndex, endIndex);

      return dataArray.slice(actualStart, actualEnd + 1);
    }

    default:
      return dataArray;
  }
};

/**
 * Create a periods mapping from a data array with {x, y} structure
 * Useful for EnhancedSlider's periods prop
 *
 * @param {Array<{x: string, y: any}>} dataArray - Array of data points
 * @returns {Array<{period: string}>} - Array suitable for EnhancedSlider
 */
export const createPeriodsFromDataArray = (dataArray) => {
  if (!dataArray || !Array.isArray(dataArray)) return [];
  return dataArray.map(point => ({ period: point.x }));
};

/**
 * Apply a period filter to a data array with {group, ...} structure (like boxplot data)
 * where group is the period string (e.g., "2014-Q1")
 *
 * @param {Array<{group: string, ...}>} dataArray - Array of data points
 * @param {Object} filter - Filter configuration
 * @returns {Array} - Filtered data array
 */
export const applyPeriodFilterToBoxplotData = (dataArray, filter) => {
  if (!dataArray || !Array.isArray(dataArray) || dataArray.length === 0) {
    return [];
  }

  if (!filter || !filter.type) {
    return dataArray;
  }

  switch (filter.type) {
    case 'lastX': {
      const count = Math.min(filter.value || dataArray.length, dataArray.length);
      return dataArray.slice(-count);
    }

    case 'year': {
      const targetYear = normalizeYear(filter.value);
      if (!targetYear) return dataArray;

      return dataArray.filter(point => {
        const pointYear = normalizeYear(extractYear(point.group));
        return pointYear === targetYear;
      });
    }

    case 'range': {
      const startPeriod = filter.start;
      const endPeriod = filter.end;

      if (!startPeriod || !endPeriod) return dataArray;

      const startIndex = dataArray.findIndex(point => point.group === startPeriod);
      const endIndex = dataArray.findIndex(point => point.group === endPeriod);

      if (startIndex === -1 || endIndex === -1) {
        console.warn('Period range not found in boxplot data:', { startPeriod, endPeriod });
        return dataArray;
      }

      const actualStart = Math.min(startIndex, endIndex);
      const actualEnd = Math.max(startIndex, endIndex);

      return dataArray.slice(actualStart, actualEnd + 1);
    }

    default:
      return dataArray;
  }
};

/**
 * Create a periods mapping from a boxplot data array with {group, ...} structure
 * Useful for EnhancedSlider's periods prop
 *
 * @param {Array<{group: string, ...}>} dataArray - Array of boxplot data points
 * @returns {Array<{period: string}>} - Array suitable for EnhancedSlider
 */
export const createPeriodsFromBoxplotData = (dataArray) => {
  if (!dataArray || !Array.isArray(dataArray)) return [];
  return dataArray.map(point => ({ period: point.group }));
};
